import type { DataSource, DataSourceInputParameter, MergedDataSourceInputParameter } from '@/types'
import { dataSourceApi } from '@/api/services'
import { isInjectedUserIdParameter } from '@/lib/injectedParameters'
import { isDropdownParameterType } from '@/lib/sqlPlaceholders'

function isHiddenParameter(p: { name: string; visible?: boolean }): boolean {
  if (p.visible === false) return true
  if (p.visible === undefined) return isInjectedUserIdParameter(p.name)
  return false
}

function mergeCaptionAcrossSources(name: string, current: string, incoming: string): string {
  const a = (current ?? '').trim()
  const b = (incoming ?? '').trim() || name
  if (!a) return b
  if (b && a !== b) {
    console.warn(
      `[report studio] Parameter "${name}" has conflicting caption across data sources; using the first definition.`
    )
  }
  return a
}

/** Hidden/injected parameter names resolved server-side (e.g. userId from JWT). */
export function collectAutoResolvedParameterNames(sources: DataSource[]): Set<string> {
  const names = new Set<string>()
  for (const ds of sources) {
    for (const p of ds.inputParameters ?? []) {
      const name = p.name.trim()
      if (!name || !isHiddenParameter(p)) continue
      names.add(name)
    }
  }
  return names
}

/** Merge input parameter definitions from several data sources; duplicate names use OR for isRequired. */
export function mergeDataSourceInputParameters(sources: DataSource[]): MergedDataSourceInputParameter[] {
  const map = new Map<string, MergedDataSourceInputParameter>()

  for (const ds of sources) {
    for (const p of ds.inputParameters ?? []) {
      const name = p.name.trim()
      if (!name || isHiddenParameter(p)) continue

      const existing = map.get(name)
      if (!existing) {
        map.set(name, {
          name,
          caption: (p.caption ?? '').trim() || name,
          isRequired: p.isRequired,
          parameterType: p.parameterType ?? 'Dropdown',
          optionsSql: p.optionsSql ?? null,
          dependsOn: p.dependsOn ?? [],
          optionSourceId: isDropdownParameterType(p.parameterType) && (p.optionsSql?.trim() ?? '')
            ? ds.id
            : undefined,
        })
        continue
      }

      existing.isRequired = existing.isRequired || p.isRequired
      existing.caption = mergeCaptionAcrossSources(name, existing.caption, p.caption ?? '')

      const incomingDrop =
        isDropdownParameterType(p.parameterType) && (p.optionsSql?.trim() ?? '').length > 0
      const existingDrop =
        isDropdownParameterType(existing.parameterType) && (existing.optionsSql?.trim() ?? '').length > 0

      if (!existingDrop && incomingDrop) {
        existing.parameterType = p.parameterType ?? 'Dropdown'
        existing.optionsSql = p.optionsSql ?? null
        existing.dependsOn = p.dependsOn ?? []
        existing.optionSourceId = ds.id
      } else if (
        existingDrop &&
        incomingDrop &&
        (existing.optionsSql !== p.optionsSql || existing.parameterType !== p.parameterType)
      ) {
        console.warn(
          `[report studio] Parameter "${name}" has conflicting dropdown metadata across data sources; using the first definition.`
        )
      }
    }
  }

  return sortMergedParametersTopologically([...map.values()])
}

/** Topological order: dependencies before dependents; then stable by name among incomparable nodes. */
export function sortMergedParametersTopologically(
  defs: MergedDataSourceInputParameter[]
): MergedDataSourceInputParameter[] {
  const byName = new Map(defs.map(d => [d.name, d]))
  const visiting = new Set<string>()
  const visited = new Set<string>()
  const ordered: MergedDataSourceInputParameter[] = []

  function visit(n: string) {
    if (visited.has(n)) return
    if (visiting.has(n)) return
    visiting.add(n)
    const d = byName.get(n)
    const deps = d?.dependsOn ?? []
    for (const x of deps) {
      if (byName.has(x)) visit(x)
    }
    visiting.delete(n)
    visited.add(n)
    if (d) ordered.push(d)
  }

  const names = [...byName.keys()].sort((a, b) => a.localeCompare(b))
  for (const n of names) visit(n)
  return ordered
}

/** Load data sources used by a template (match by key). One list request per key with filter, then exact key match. */
export async function fetchDataSourcesByKeys(keys: string[]): Promise<DataSource[]> {
  if (keys.length === 0) return []
  const unique = [...new Set(keys)]
  const results = await Promise.all(
    unique.map(async key => {
      const res = await dataSourceApi.getAll({ filter: key, pageNumber: 1, pageSize: 100 })
      return res.items.find(ds => ds.key === key) ?? null
    })
  )
  return results.filter((x): x is DataSource => x != null)
}

export function areRequiredParametersSatisfied(
  defs: DataSourceInputParameter[],
  parameters: Record<string, string>
): boolean {
  return defs.filter(d => d.isRequired).every(d => (parameters[d.name] ?? '').trim() !== '')
}

export function hasOnlyOptionalParameters(defs: DataSourceInputParameter[]): boolean {
  return defs.length > 0 && defs.every(d => !d.isRequired)
}
